import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import type { TariffRateAnalysisRow } from '../../../apis/types'
import { RateEnumBadge, TARIFF_TABLE_ROW_HOVER, TariffStatusChip } from './TariffChips'
import { SortableColumnHeader } from './TariffFieldKey'
import { nextSortState, type SortState, type ValidationTableSortKey } from './tariffRateSort'
import { TariffRateTrendChart } from './TariffRateTrendChart'
import {
    DELTA_TONE_CLASS,
    buildValidationScheduleGroups,
    deltaTone,
    formatDelta,
    formatRate,
    isOlderHiddenRateset,
    type ValidationRatesetGroup,
    type ValidationScheduleGroup,
} from './tariffRateValidation'

const tdBase = 'px-2 py-1 text-xs text-slate-700 dark:text-slate-200'
const tdClass = `${tdBase} ${TARIFF_TABLE_ROW_HOVER}`
const tdMonoClass = `${tdClass} font-mono tabular-nums`

const SORT_COLUMNS: ReadonlyArray<{
    label: string
    sortKey: ValidationTableSortKey
    vintageOnly?: boolean
}> = [
    { label: 'Season', sortKey: 'season' },
    { label: 'TOU', sortKey: 'touPeriod' },
    { label: 'Total', sortKey: 'totalRate' },
    { label: 'Generation', sortKey: 'generationRate' },
    { label: 'Delivery', sortKey: 'deliveryRate' },
    { label: 'NBC', sortKey: 'nbcRate' },
    { label: 'Delivery+NBC', sortKey: 'deliveryPlusNbc' },
    { label: 'Delivery+NBC-WF', sortKey: 'deliveryPlusNbcWithoutWildfire' },
    { label: 'PCIA year', sortKey: 'vintageYear', vintageOnly: true },
    { label: 'Generation Delta', sortKey: 'generationDelta' },
    { label: 'Delivery Delta', sortKey: 'deliveryDelta' },
    { label: 'NBC Delta', sortKey: 'nbcDelta' },
]

function olderRatesetCount(group: ValidationScheduleGroup, olderKeys: Set<string>): number {
    return group.ratesets.reduce((count, rateset) => count + (olderKeys.has(rateset.key) ? 1 : 0), 0)
}

function useHasBeenInView(): [(node: HTMLElement | null) => void, boolean] {
    const [node, setNode] = useState<HTMLElement | null>(null)
    const [seen, setSeen] = useState(false)
    useEffect(() => {
        if (node == null || seen) return
        if (typeof IntersectionObserver === 'undefined') {
            setSeen(true)
            return
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) setSeen(true)
            },
            { rootMargin: '200px 0px' }
        )
        observer.observe(node)
        return () => observer.disconnect()
    }, [node, seen])
    return [setNode, seen]
}

export const TariffRateValidationTable: React.FC<{
    rows: TariffRateAnalysisRow[]
    showSchedule?: boolean
    loading?: boolean
    description?: string
    /** Injected for tests. Defaults to now. */
    now?: Date
}> = ({ rows, showSchedule = false, loading = false, description, now }) => {
    const asOf = useMemo(() => now ?? new Date(), [now])
    const hasVintage = useMemo(() => rows.some((row) => row.vintageYear != null), [rows])
    const [sort, setSort] = useState<SortState<ValidationTableSortKey> | null>(null)
    const scheduleGroups = useMemo(() => buildValidationScheduleGroups(rows, sort), [rows, sort])

    const newestBySchedule = useMemo(() => {
        const newest = new Map<string, string>()
        for (const group of scheduleGroups) {
            const date = group.ratesets[0]?.rateEffectiveDate
            if (date != null) newest.set(group.schedule, date)
        }
        return newest
    }, [scheduleGroups])

    const olderKeys = useMemo(() => {
        const keys = new Set<string>()
        for (const group of scheduleGroups) {
            const newest = newestBySchedule.get(group.schedule)
            if (newest == null) continue
            for (const rateset of group.ratesets) {
                if (isOlderHiddenRateset(rateset.rateEffectiveDate, newest, asOf)) {
                    keys.add(rateset.key)
                }
            }
        }
        return keys
    }, [scheduleGroups, newestBySchedule, asOf])

    const groupKeySignature = scheduleGroups
        .map((group) => `${group.schedule}:${group.ratesets.map((rateset) => rateset.key).join(',')}`)
        .join('\n')
    const [collapsedSchedules, setCollapsedSchedules] = useState<Set<string>>(() => new Set())
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set())
    const [showOlderSchedules, setShowOlderSchedules] = useState<Set<string>>(() => new Set())

    useEffect(() => {
        const collapsed = new Set<string>()
        for (const line of groupKeySignature.split('\n')) {
            const keys = line.split(':')[1]?.split(',').filter(Boolean) ?? []
            for (const key of keys.slice(1)) collapsed.add(key)
        }
        setCollapsedGroups(collapsed)
        setCollapsedSchedules(new Set())
        setShowOlderSchedules(new Set())
    }, [groupKeySignature])

    const allRatesetKeys = useMemo(
        () => scheduleGroups.flatMap((group) => group.ratesets.map((rateset) => rateset.key)),
        [scheduleGroups]
    )
    const allScheduleKeys = useMemo(
        () => scheduleGroups.map((group) => group.schedule),
        [scheduleGroups]
    )

    const toggleSchedule = (schedule: string): void => {
        setCollapsedSchedules((prev) => {
            const next = new Set(prev)
            if (next.has(schedule)) next.delete(schedule)
            else next.add(schedule)
            return next
        })
    }

    const toggleGroup = (key: string): void => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const toggleOlder = (schedule: string): void => {
        setShowOlderSchedules((prev) => {
            const next = new Set(prev)
            if (next.has(schedule)) next.delete(schedule)
            else next.add(schedule)
            return next
        })
    }

    const colSpan = 8 + (hasVintage ? 1 : 0) + 3

    const descriptionCopy =
        description != null ? (
            <p className="min-w-0 text-sm text-slate-500 dark:text-slate-400">{description}</p>
        ) : null

    if (loading) {
        return (
            <div className="space-y-3">
                {descriptionCopy}
                <div className="animate-pulse h-32 rounded-md bg-slate-200 dark:bg-slate-700" />
            </div>
        )
    }

    if (rows.length === 0) {
        return (
            <div className="space-y-3">
                {descriptionCopy}
                <p className="px-1 py-3 text-sm text-slate-500 dark:text-slate-400">
                    No generation, delivery, and NBC TOU rows to validate on this tariff.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            <div className="flex w-full items-baseline gap-4">
                {descriptionCopy}
                <div className="ml-auto flex shrink-0 items-center gap-2 text-xs">
                    <button
                        type="button"
                        onClick={() => {
                            setCollapsedGroups(new Set())
                            setCollapsedSchedules(new Set())
                        }}
                        className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                    >
                        Expand all
                    </button>
                    <span aria-hidden="true">·</span>
                    <button
                        type="button"
                        onClick={() => {
                            setCollapsedGroups(new Set(allRatesetKeys))
                            if (showSchedule) setCollapsedSchedules(new Set(allScheduleKeys))
                        }}
                        className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                    >
                        Collapse all
                    </button>
                </div>
            </div>
            <div className="space-y-8">
                {scheduleGroups.map((group) => (
                    <ScheduleTable
                        key={group.schedule}
                        group={group}
                        showSchedule={showSchedule}
                        showOlder={showOlderSchedules.has(group.schedule)}
                        olderCount={olderRatesetCount(group, olderKeys)}
                        olderKeys={olderKeys}
                        scheduleExpanded={!showSchedule || !collapsedSchedules.has(group.schedule)}
                        collapsedGroups={collapsedGroups}
                        colSpan={colSpan}
                        hasVintage={hasVintage}
                        sort={sort}
                        onSort={(key) => setSort((prev) => nextSortState(prev, key))}
                        onToggleSchedule={() => toggleSchedule(group.schedule)}
                        onToggleGroup={toggleGroup}
                        onToggleOlder={() => toggleOlder(group.schedule)}
                    />
                ))}
            </div>
        </div>
    )
}

const ValidationColumnHeaders: React.FC<{
    sort: SortState<ValidationTableSortKey> | null
    hasVintage: boolean
    onSort: (key: ValidationTableSortKey) => void
}> = ({ sort, hasVintage, onSort }) => (
    <thead className="bg-slate-50 dark:bg-slate-900/60">
        <tr className="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {SORT_COLUMNS.map((column) =>
                column.vintageOnly === true && !hasVintage ? null : (
                    <SortableColumnHeader
                        key={column.sortKey}
                        label={column.label}
                        sortKey={column.sortKey}
                        activeKey={sort?.key ?? null}
                        dir={sort?.dir ?? null}
                        onSort={(key) => onSort(key as ValidationTableSortKey)}
                        className="px-2 py-2"
                    />
                )
            )}
        </tr>
    </thead>
)

const ScheduleTable: React.FC<{
    group: ValidationScheduleGroup
    showSchedule: boolean
    showOlder: boolean
    olderCount: number
    olderKeys: Set<string>
    scheduleExpanded: boolean
    collapsedGroups: Set<string>
    colSpan: number
    hasVintage: boolean
    sort: SortState<ValidationTableSortKey> | null
    onSort: (key: ValidationTableSortKey) => void
    onToggleSchedule: () => void
    onToggleGroup: (key: string) => void
    onToggleOlder: () => void
}> = ({
    group,
    showSchedule,
    showOlder,
    olderCount,
    olderKeys,
    scheduleExpanded,
    collapsedGroups,
    colSpan,
    hasVintage,
    sort,
    onSort,
    onToggleSchedule,
    onToggleGroup,
    onToggleOlder,
}) => {
    const visibleRatesets = showOlder
        ? group.ratesets
        : group.ratesets.filter((rateset) => !olderKeys.has(rateset.key))
    const chartRows = useMemo(
        () => group.ratesets.flatMap((rateset) => rateset.rows),
        [group.ratesets]
    )
    const [sectionRef, chartReady] = useHasBeenInView()
    const headingId = `rate-validation-${group.schedule}`
    const moreLabel = showOlder
        ? 'Show less'
        : `...and ${olderCount} more`
    const moreAria = showOlder
        ? `Hide older ratesets for ${group.schedule}`
        : `Show ${olderCount} older rateset${olderCount === 1 ? '' : 's'} for ${group.schedule}`

    return (
        <section
            ref={sectionRef}
            className="space-y-2"
            aria-labelledby={showSchedule ? headingId : undefined}
        >
            {showSchedule ? (
                <div className="flex w-full items-center gap-2">
                    <button
                        type="button"
                        id={headingId}
                        onClick={onToggleSchedule}
                        aria-expanded={scheduleExpanded}
                        aria-label={
                            group.tariffName != null && group.tariffName !== ''
                                ? `${group.schedule} ${group.tariffName}`
                                : group.schedule
                        }
                        className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm font-semibold tracking-wide text-slate-800 hover:text-slate-950 dark:text-slate-100 dark:hover:text-white"
                    >
                        {scheduleExpanded ? (
                            <ChevronDownIcon className="h-4 w-4 shrink-0" />
                        ) : (
                            <ChevronRightIcon className="h-4 w-4 shrink-0" />
                        )}
                        <span className="font-mono">{group.schedule}</span>
                        {group.tariffName != null && group.tariffName !== '' ? (
                            <span className="min-w-0 truncate font-normal font-sans tracking-normal text-slate-700 dark:text-slate-200">
                                {group.tariffName}
                            </span>
                        ) : null}
                    </button>
                    {group.tariffStatus != null ? <TariffStatusChip status={group.tariffStatus} /> : null}
                </div>
            ) : null}
            {scheduleExpanded ? (
                <div className="space-y-3">
                    {/* Graph every rateset, including those the table hides behind "...and n more". */}
                    {chartReady ? (
                        <TariffRateTrendChart rows={chartRows} embedded={!showSchedule} />
                    ) : null}
                    <div className="relative isolate overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
                        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                            <ValidationColumnHeaders sort={sort} hasVintage={hasVintage} onSort={onSort} />
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {visibleRatesets.map((rateset) => (
                                    <RatesetGroupRows
                                        key={rateset.key}
                                        rateset={rateset}
                                        dateOnly={showSchedule}
                                        expanded={!collapsedGroups.has(rateset.key)}
                                        colSpan={colSpan}
                                        hasVintage={hasVintage}
                                        onToggle={() => onToggleGroup(rateset.key)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {olderCount > 0 ? (
                        <button
                            type="button"
                            onClick={onToggleOlder}
                            aria-label={moreAria}
                            className="text-xs font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                        >
                            {moreLabel}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </section>
    )
}

const RatesetGroupRows: React.FC<{
    rateset: ValidationRatesetGroup
    dateOnly: boolean
    expanded: boolean
    colSpan: number
    hasVintage: boolean
    onToggle: () => void
}> = ({ rateset, dateOnly, expanded, colSpan, hasVintage, onToggle }) => {
    const label = dateOnly ? rateset.rateEffectiveDate : `${rateset.schedule} · ${rateset.rateEffectiveDate}`
    return (
        <React.Fragment>
            <tr className="bg-slate-200/90 dark:bg-slate-700/90">
                <td colSpan={colSpan} className="p-0">
                    <div className="flex w-full items-center gap-2 px-2 py-1.5">
                        <button
                            type="button"
                            onClick={onToggle}
                            aria-expanded={expanded}
                            className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-semibold tracking-wide text-slate-800 hover:text-slate-950 dark:text-slate-100 dark:hover:text-white"
                        >
                            {expanded ? (
                                <ChevronDownIcon className="h-4 w-4 shrink-0" />
                            ) : (
                                <ChevronRightIcon className="h-4 w-4 shrink-0" />
                            )}
                            <span>{label}</span>
                        </button>
                        {rateset.ratesetStatus != null ? (
                            <TariffStatusChip status={rateset.ratesetStatus} />
                        ) : null}
                    </div>
                </td>
            </tr>
            {expanded
                ? rateset.rows.map((row, index) => (
                      <tr key={`${rateset.key}-${index}`} className="group">
                          <td className={tdClass}>
                              <RateEnumBadge kind="season" label={row.season} />
                          </td>
                          <td className={tdClass}>
                              <RateEnumBadge kind="tou" label={row.touPeriod} />
                          </td>
                          <td className={tdMonoClass}>{formatRate(row.totalRate)}</td>
                          <td className={tdMonoClass}>{formatRate(row.generationRate)}</td>
                          <td className={tdMonoClass}>{formatRate(row.deliveryRate)}</td>
                          <td className={tdMonoClass}>{formatRate(row.nbcRate)}</td>
                          <td className={tdMonoClass}>{formatRate(row.deliveryPlusNbc)}</td>
                          <td className={tdMonoClass}>{formatRate(row.deliveryPlusNbcWithoutWildfire)}</td>
                          {hasVintage ? <td className={tdClass}>{row.vintageYear ?? 'N/A'}</td> : null}
                          <DeltaCell
                              delta={row.generationDelta}
                              pct={row.generationDeltaPct}
                              previous={row.previousEffectiveDate}
                              previousValue={
                                  row.generationDelta != null ? row.generationRate - row.generationDelta : null
                              }
                          />
                          <DeltaCell
                              delta={row.deliveryDelta}
                              pct={row.deliveryDeltaPct}
                              previous={row.previousEffectiveDate}
                              previousValue={
                                  row.deliveryDelta != null ? row.deliveryRate - row.deliveryDelta : null
                              }
                          />
                          <DeltaCell
                              delta={row.nbcDelta}
                              pct={row.nbcDeltaPct}
                              previous={row.previousEffectiveDate}
                              previousValue={row.nbcDelta != null ? row.nbcRate - row.nbcDelta : null}
                          />
                      </tr>
                  ))
                : null}
        </React.Fragment>
    )
}

const DeltaCell: React.FC<{
    delta: number | null
    pct: number | null
    previous: string | null
    previousValue: number | null
}> = ({ delta, pct, previous, previousValue }) => {
    const tone = deltaTone(delta)
    const title =
        previous != null && previousValue != null
            ? `Previous ${previous}: ${previousValue.toFixed(6)} (${pct != null ? `${(pct * 100).toFixed(1)}%` : 'n/a'})`
            : undefined
    return (
        <td
            className={`${tdBase} whitespace-nowrap font-mono tabular-nums ${DELTA_TONE_CLASS[tone]} ${TARIFF_TABLE_ROW_HOVER}`}
            title={title}
        >
            {formatDelta(delta)}
        </td>
    )
}
